import { Directive, EventEmitter, Input, type OnInit, Output, inject } from '@angular/core';
import {
    IAttributeConfData,
    IAttributeConfDataOption,
    ProductConfigurableOptionsComponentDef
} from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import { IProductConfigurableSelection, type IProductDataV3, type Product } from '@df/catalog/product/product';
import { DfEventService } from '@df/core/service/df-event';
import { AbService } from '@df/module-ab/ab.service';
import { IAddToBasketData } from '@df/session/api/basket/basket';
import { BasketService } from '@df/session/api/basket/basket.service';
import { Customer } from '@df/session/api/customer';
import difference from 'lodash-es/difference';
import { ProductDecorator } from '../../../model/product/product.decorator';
import { IParentView } from '../view/layout/product-view-layout.abstract';
import { SWATCH_TYPE_OPTIONS } from '../view/layout/product-view-layout.interface';

@Directive()
export class ProductConfigurableOptionsAbstract extends ProductConfigurableOptionsComponentDef<Product<IProductDataV3>> implements OnInit {
    /**
     * Exclude options.
     */
    @Input()
    excludeOptions: string[] = [];

    @Input()
    sizeGuideVariant?: string;

    @Input()
    parentView?: IParentView;

    @Output()
    sizeGuideClicked = new EventEmitter<string>();

    protected basketService = inject(BasketService);
    protected customer = inject(Customer);
    protected abService = inject(AbService);
    protected dfEventService = inject(DfEventService);
    /**
     * If attribute should be presented with model
     */
    isSwatchTypeAttribute(attribute: IAttributeConfData): boolean {
        return SWATCH_TYPE_OPTIONS.includes(attribute.code);
    }

    isModernOptionType(attribute: IAttributeConfData): boolean | undefined {
        return ['letter', 'color'].indexOf(attribute.code) !== -1 && !!(<ProductDecorator>this.parent).hasLetterOption;
    }

    /**
     * Checks if option can be picked
     */
    override canUseOption(attributeCode: string, optionValue: number): boolean {
        const mapItem = this.attributes.find(i => i.code === attributeCode);

        if (mapItem) {
            const option = mapItem.options.find(i => i.value === optionValue);

            return !!option?.product;
        }

        return false;
    }

    sizeGuideClickHolder(variant: string) {
        this.sizeGuideClicked.emit(variant);
    }

    hasAllSelection(): boolean {
        return this.attributes.every(attribute => this.hasSelection(attribute));
    }

    openXnotifEmail() {
        this.app.events.broadcast('xnotif-open');
    }

    /**
     * Normalizes whatever we passed here to IAddToBasketData[]
     * @return {IAddToBasketData[]}
     */
    getRequest(): IAddToBasketData[] | undefined {
        const buyRequest = this.parentView?.request;

        const requests = this.customer.basket.normalizeAddToBasketData({
            id: this.product ? this.product.id : 0,
            qty: 1,
            options: buyRequest ? buyRequest.options : undefined,
            bundle_options: buyRequest ? buyRequest.bundle_options : undefined
        });

        if (requests) {
            return requests.filter(i => i.qty > 0);
        }

        return requests;
    }

    protected override setData(data: IProductConfigurableSelection) {
        super.setData(data);

        this.parentView?.handleDataChange(data);
    }

    protected override setAttributes(attributes: IAttributeConfData[] | undefined) {
        if (!attributes) {
            attributes = [];
        }

        const orderedAttrs = ['color', 'size'].map(i => attributes?.find(a => a.code === i)).filter(i => !!i);

        attributes = [...orderedAttrs, ...difference(attributes, orderedAttrs)];

        super.setAttributes(attributes?.filter(i => !this.excludeOptions.includes(i.code)) || []);
    }

    protected override getOptionsMap(code: string): IAttributeConfDataOption[] {
        return super.getOptionsMap(code).filter(i => i.product);
    }
}
